import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Container,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Badge,
} from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import ActionButton from '../../Components/Common/ActionButton';
import ListPager from '../../Components/Common/ListPager';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListSearch,
} from '../../Components/Common/ListFilters';
import metaMask from '../../assets/images/nft/newrecord.png';
import notify, { MESSAGES } from '../../utils/notify';
import Select from 'react-select';
import axios from 'axios';
import { api, web } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';
import '../TenderOpening/components/TenderOpening.css';
import { Link } from 'react-router-dom';
import UserFormModal from './component/UserFormModal';
import IdamanUserFormModal from './component/IdamanUserFormModal';

const ProcurementList = () => {
  const token = getAuthToken();
  const [data, setData] = useState({ items: [] });
  const [modal, setModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isLoading, setIsLoading] = useState(false);
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showIdamanModal, setShowIdamanModal] = useState(false);
  const [formData, setFormData] = useState({
    titleProcurement: '',
    procurementType: '',
    description: '',
  });
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  // Debounce search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  const procurementtype = [
    {
      options: [
        { label: 'Select Procurement Type', value: '' },
        { label: 'Pelelangan Khusus', value: 'PelelanganKhusus' },
        { label: 'Pemilihan Langsung', value: 'PemilihanLangsung' },
        { label: 'Tunjuk Langsung', value: 'TunjukLangsung' },
      ],
    },
  ];

  const togglemodal = () => {
    setModal(!modal);
  };

  const columns = [
    {
      name: 'User Name',
      selector: row => row.userName,
      sortable: true,
      width: '15%',
      wrap: true,
      cell: row => (
        <span className='to-title' title={String(row.userName ?? '')}>
          {row.userName ?? '-'}
        </span>
      ),
    },
    {
      name: 'Email',
      selector: row => row.email,
      sortable: true,
      width: '15%',
      grow: 1,
      wrap: true,
      cell: row => (
        <span className='text-break' title={row.email}>
          {row.email ?? '-'}
        </span>
      ),
    },
    {
      name: 'Display Name',
      selector: row => row.procurementType,
      sortable: true,
      width: '10%',
      wrap: true,
      omit: window.innerWidth < 768,
      cell: row => (
        <span className='text-break' title={row.displayName}>
          {row.displayName ?? '-'}
        </span>
      ),
    },
    {
      name: 'Company/Job Title',
      selector: row => row.productType?.name,
      sortable: true,
      width: '20%',
      wrap: true,
      omit: window.innerWidth < 992,
      cell: row => (
        <div>
          <span className='fw-medium d-block text-break'>
            {row.companyName || '-'}
          </span>
          {row.jobTitle && (
            <p className='text-muted mb-0 small text-break'>{row.jobTitle}</p>
          )}
        </div>
      ),
    },

    {
      name: 'Type',
      width: '10%',
      sortable: false,
      center: false,
      cell: row => {
        const isMitra = row.roles?.some(
          role => role.toLowerCase() === 'registered supplier'
        );
        return (
          <span className={`to-status${isMitra ? '' : ' to-status--info'}`}>
            {isMitra ? 'MITRA' : 'PERTAMINA'}
          </span>
        );
      },
    },

    {
      name: 'Role',
      width: '20%',
      sortable: false,
      center: false,
      cell: row => {
        return (
          <div className='d-flex flex-wrap gap-1'>
            {row.roles?.length > 0
              ? row.roles.map((role, idx) => (
                  <span key={idx} className='to-tag'>
                    {role}
                  </span>
                ))
              : '-'}
          </div>
        );
      },
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      width: '10%',
      center: true,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <Link
            to={`../apps-usermanagement/dtl/${row?.id || ''}`}
            className='btn btn-sm btn-ghost-secondary btn-icon hy-icon-action'
            aria-label='View'
            title='View'
          >
            <i className='ri-eye-line align-middle' aria-hidden='true'></i>
          </Link>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit'
            onClick={() => handleEditClick(row)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            danger
            icon='ri-delete-bin-line'
            label='Delete'
            confirm={{
              title: 'Delete this user?',
              message: 'This user will be removed and cannot be restored.',
              subject: row.displayName || row.userName,
              confirmLabel: 'Delete user',
            }}
            onClick={() => handleDelete(row.id)}
          />
          {row.roles?.some(
            role => role.toLowerCase() === 'registered supplier'
          ) && (
            <ActionButton
              variant='icon'
              size='sm'
              icon='ri-shield-keyhole-line'
              label='Reset MFA'
              confirm={{
                title: 'Reset two-factor authentication?',
                message:
                  'The current authenticator app and recovery codes will stop working. The user must set up the authenticator app again on the next sign in.',
                subject: row.displayName || row.userName,
                confirmLabel: 'Reset MFA',
              }}
              onClick={() => handleResetMfa(row.id)}
            />
          )}
        </div>
      ),
    },
  ];

  document.title = 'List User Management ';

  const handleprocurementTypeChange = selectedOption => {
    setFormData({ ...formData, procurementType: selectedOption.value });
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateFields = () => {
    let tempErrors = {};
    if (!formData.titleProcurement) {
      tempErrors.titleProcurement = 'Please Enter Title';
    }
    if (!formData.procurementType) {
      tempErrors.procurementType = 'Please Select ';
    }

    if (!formData.description) {
      tempErrors.description = 'Please Enter Description';
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const closeModal = () => {
    setFormData({
      titleProcurement: '',
      procurementType: '',
    });
    setErrors({});
    setModal(!modal);
  };

  const handleSubmit = async e => {
    if (validateFields()) {
      e.preventDefault();
      try {
        setIsLoading(true);
        const response = await axios.post(
          `${api.API_URL}/procurement`,
          formData,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.success) {
          notify.success(MESSAGES.saved);
          window.location = `${web.WEB_URL}/apps-procurement/upd/${response.data.id}`;
        } else {
          notify.error('Failed to save data. Please try again.');
        }
      } catch (error) {
        notify.apiError(error, 'save data');
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData(
      currentPage,
      rowsPerPage,
      token,
      debouncedSearchText,
      sortField,
      sortDirection
    );
  }, [
    currentPage,
    rowsPerPage,
    token,
    debouncedSearchText,
    sortField,
    sortDirection,
  ]);

  const fetchData = async (
    currentPage,
    rowsPerPage,
    token,
    search = '',
    sort = '',
    direction = 'asc'
  ) => {
    setIsLoading(true);
    let url = `${api.API_URL}/appuser?PageNumber=${currentPage}&PageSize=${rowsPerPage}`;
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    if (sort && sort !== '') {
      url += `&sortBy=${sort}&sortDirection=${direction}`;
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      setData({
        items: result.data?.items || [],
      });
      setTotalRows(result.data?.totalItems || 0);
    } catch (error) {
      notify.apiError(error, 'load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = user => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleDelete = async id => {
    // Confirmation is handled by the ActionButton dialog.
    try {
      const res = await fetch(`${api.API_URL}/appuser/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchData(
          currentPage,
          rowsPerPage,
          token,
          debouncedSearchText,
          sortField,
          sortDirection
        );
        notify.success('User deleted successfully.');
      } else {
        notify.apiError(
          `Request failed with status code ${res.status}`,
          'delete the user'
        );
      }
    } catch (e) {
      notify.apiError(e, 'delete the user');
    }
  };

  // Supplier kehilangan HP/aplikasi authenticator (BE: Super Admin only).
  const handleResetMfa = async id => {
    try {
      await axios.post(`${api.API_URL}/account/reset-mfa/${id}`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notify.success('MFA reset successfully.');
    } catch (e) {
      notify.apiError(e, 'reset MFA');
    }
  };

  const handleSort = (column, sortDirection) => {
    const field = column.selector
      ? column.selector.toString().split('.').pop()
      : '';
    setSortField(field);
    setSortDirection(sortDirection);
  };

  const handleSearch = e => {
    setSearchText(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const clearFilters = () => {
    setSearchText('');
    setSortField('');
    setSortDirection('asc');
    setCurrentPage(1);
  };

  const filterChips = [
    searchText && {
      key: 'search',
      label: 'Search',
      value: searchText,
      onRemove: () => handleSearch({ target: { value: '' } }),
    },
  ].filter(Boolean);

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='to-hero mb-4'>
                  <PageBreadcrumb
                    fallback={{
                      label: 'User Management',
                      to: '/apps-usermanagement/view',
                    }}
                  />

                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='to-hero-icon'>
                      <i className='ri-user-settings-line' aria-hidden='true' />
                    </span>
                    <div className='flex-grow-1'>
                      <h4 className='to-hero-title'>User Management</h4>
                      <p className='to-hero-subtitle'>
                        Manage application users, their companies and roles.
                      </p>
                    </div>
                    <div className='d-flex gap-2 flex-wrap'>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={() =>
                          fetchData(
                            currentPage,
                            rowsPerPage,
                            token,
                            debouncedSearchText,
                            sortField,
                            sortDirection
                          )
                        }
                      >
                        <i className='ri-refresh-line' aria-hidden='true' />
                        Refresh
                      </button>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={() => setShowIdamanModal(true)}
                      >
                        <i className='ri-user-add-line' aria-hidden='true' />
                        Add from idAMan
                      </button>
                      <button
                        type='button'
                        className='to-hero-btn to-hero-btn--solid'
                        onClick={() => setShowAddModal(true)}
                      >
                        <i className='ri-add-line' aria-hidden='true' />
                        Create New User
                      </button>
                    </div>
                  </div>
                </div>

                <div className='to-panel'>
                  <div className='to-toolbar'>
                    <ListSearch
                      value={searchText}
                      onChange={handleSearch}
                      placeholder='Search by user name or email...'
                      ariaLabel='Search by user name or email'
                    />
                    <ClearFiltersButton
                      count={filterChips.length}
                      onClick={clearFilters}
                    />
                    <span className='to-toolbar-meta'>
                      {isLoading
                        ? 'Loading...'
                        : `Showing ${startEntry}–${endEntry} of ${totalRows} users`}
                    </span>
                  </div>

                  <ActiveFilterChips chips={filterChips} />

                  <div className='to-table-wrap'>
                    <DataTable
                      columns={columns}
                      data={data.items}
                      onSort={handleSort}
                      sortServer
                      highlightOnHover
                      persistTableHead
                      progressPending={isLoading}
                      progressComponent={<SkeletonRows />}
                      noDataComponent={
                        <EmptyState
                          title='No user found'
                          emptyHint='No application users have been added yet. Create a user manually, or import an existing account from idAMan.'
                          filteredHint={`No user matches the search "${searchText}". Try a user name, email or a shorter keyword.`}
                          hasFilters={filterChips.length > 0}
                          onClear={clearFilters}
                          action={{
                            label: 'Create New User',
                            onClick: () => setShowAddModal(true),
                          }}
                          secondaryAction={{
                            label: 'Add from idAMan',
                            icon: 'ri-user-add-line',
                            onClick: () => setShowIdamanModal(true),
                          }}
                        />
                      }
                      customStyles={{
                        headCells: {
                          style: { paddingLeft: '12px', paddingRight: '12px' },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                          },
                        },
                        table: { style: { width: '100%' } },
                      }}
                    />
                  </div>

                  <ListPager
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    isLoading={isLoading}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePerRowsChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
      <Modal
        size='lg'
        isOpen={modal}
        role='dialog'
        autoFocus={true}
        centered
        id='addAddressModal'
        toggle={togglemodal}
        data-bs-backdrop='static'
      >
        <ModalHeader
          toggle={() => {
            setModal(!modal);
          }}
        ></ModalHeader>
        <ModalBody>
          <div className='d-flex mt-2'>
            <Col xxl={12}>
              <div>
                <Row>
                  <Row className='justify-content-center mb-1'>
                    <Col lg={12} className='text-center'>
                      <img
                        src={metaMask}
                        alt=''
                        height='65'
                        className='mb-3 pb-2'
                      />
                      <h5>Create New Planning & Procurement </h5>
                      <p className='text-muted pb-1'>
                        Start a new procurement request by filling in the
                        details below.
                      </p>
                    </Col>
                  </Row>
                  <Col lg={12}>
                    <div className='mb-2 bor border-top  border-top-dashed '></div>
                  </Col>
                  <Col sm={6}>
                    <div className='mb-2'>
                      <Label htmlFor='titleProcurement' className='form-label'>
                        Title Procurement
                        {errors.titleProcurement && (
                          <Badge color='warning' className='badge-label '>
                            <i className='ri-checkbox-blank-circle-fill'></i>{' '}
                            {errors.titleProcurement}{' '}
                          </Badge>
                        )}{' '}
                      </Label>
                      <Input
                        type='text'
                        className='form-control '
                        id='titleProcurement'
                        name='titleProcurement'
                        placeholder='Enter Title Procurement'
                        onChange={e => {
                          handleChange(e);
                          setErrors({
                            ...errors,
                            titleProcurement: '',
                          });
                        }}
                      />
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className='mb-2'>
                      <span
                        htmlFor='choices-publish-status-input'
                        className='form-label'
                      >
                        Procurement Type{' '}
                        {errors.procurementType && (
                          <Badge color='warning' className='badge-label '>
                            <i className='ri-checkbox-blank-circle-fill'></i>{' '}
                            {errors.procurementType}{' '}
                          </Badge>
                        )}{' '}
                      </span>

                      <Select
                        aria-label='Procurement type'
                        name='procurementType'
                        id='procurementType'
                        placeholder='-- Please Select --'
                        options={procurementtype}
                        onChange={e => {
                          handleprocurementTypeChange(e);
                          setErrors({
                            ...errors,
                            procurementType: '',
                          });
                        }}
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    <div>
                      <Label
                        htmlFor='exampleFormControlTextarea5'
                        className='form-label'
                      >
                        Description{' '}
                        {errors.description && (
                          <Badge color='warning' className='badge-label '>
                            <i className='ri-checkbox-blank-circle-fill'></i>{' '}
                            {errors.description}{' '}
                          </Badge>
                        )}{' '}
                      </Label>
                      <textarea
                        className='form-control'
                        id='description'
                        name='description'
                        onChange={e => {
                          handleChange(e);
                          setErrors({
                            ...errors,
                            description: '',
                          });
                        }}
                        rows='5'
                      ></textarea>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className='hstack gap-2 justify-content-end d-print-none mt-4'>
            <ActionButton
              variant='secondary'
              icon='ri-close-line'
              onClick={closeModal}
            >
              Close
            </ActionButton>
            <ActionButton
              variant='primary'
              type='submit'
              className='ms-auto'
              onClick={handleSubmit}
            >
              Next
              <i
                className='ri-arrow-right-line align-middle ms-2'
                aria-hidden='true'
              ></i>
            </ActionButton>
          </div>
        </ModalFooter>
      </Modal>

      <UserFormModal
        isOpen={showAddModal}
        toggle={() => setShowAddModal(false)}
        userData={null}
        onSuccess={() => {
          fetchData(
            currentPage,
            rowsPerPage,
            token,
            debouncedSearchText,
            sortField,
            sortDirection
          );
        }}
      />

      <IdamanUserFormModal
        isOpen={showIdamanModal}
        toggle={() => setShowIdamanModal(false)}
        onSuccess={() => {
          fetchData(
            currentPage,
            rowsPerPage,
            token,
            debouncedSearchText,
            sortField,
            sortDirection
          );
        }}
      />

      <UserFormModal
        isOpen={showEditModal}
        toggle={() => setShowEditModal(false)}
        userData={selectedUser}
        onSuccess={() => {
          fetchData(
            currentPage,
            rowsPerPage,
            token,
            debouncedSearchText,
            sortField,
            sortDirection
          );
        }}
      />
    </React.Fragment>
  );
};

export default ProcurementList;
